import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { useUI } from '@contexts/ui.context';
import { useEffect, useState } from 'react';
import Image from '@components/ui/image';
import { useTranslation } from '@i18n/client';
import useQueryParam from '@utils/use-query-params';
import { FilterItem, getFilterNameForLanguage } from '@framework/types';
import { IoClose } from 'react-icons/io5';

type FilterItemWithDepth = FilterItem & { depth?: number };

export type CategoryFilterTreeItemProps = {
  className?: string;
  item: FilterItemWithDepth;
  depth?: number;
  lang: string;
};

function CategoryFilterTreeItem({
  className = '',
  item: menuItem,
  depth = 0,
  lang,
}: CategoryFilterTreeItemProps) {
  const { t } = useTranslation(lang, 'common');
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const { updateQueryParams, clearQueryParam } = useQueryParam(pathname ?? '/');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { closeFilter } = useUI();

  const hasQueryKey = searchParams?.get('category');

  useEffect(() => {
    setSelectedCategory(hasQueryKey?.split(',')[0] ?? '');
  }, [hasQueryKey]);

  function onClick() {
    if (selectedCategory === menuItem.slug) {
      setSelectedCategory('');
      clearQueryParam(['category']);
    } else {
      setSelectedCategory(menuItem.slug);
      updateQueryParams('category', menuItem.slug);
    }
    closeFilter();
  }

  function handleClearCategory(e: React.MouseEvent) {
    e.stopPropagation();
    setSelectedCategory('');
    clearQueryParam(['category']);
    closeFilter();
  }

  const isSelected = selectedCategory === menuItem.slug;
  const isTopLevel = depth === 0;

  return (
    <li
      onClick={onClick}
      className={cn(
        'flex items-center transition text-sm md:text-15px cursor-pointer list-none',
        { 'bg-brand text-brand-light': isSelected },
        className,
      )}
      style={{
        paddingLeft: `${depth * 20 + 8}px`,
        paddingRight: '12px',
        borderLeft: depth > 0 ? '2px solid #e5e7eb' : 'none',
        listStyle: 'none',
        listStyleType: 'none',
      }}
    >
      <button
        className={cn(
          'flex items-center w-full ltr:text-left rtl:text-right cursor-pointer group py-2',
        )}
      >
        {menuItem.icon && (
          <div className="inline-flex shrink-0 w-8 h-8 ltr:mr-2 rtl:ml-2">
            <Image
              src={menuItem.icon}
              alt={
                getFilterNameForLanguage(menuItem, lang) ||
                t('text-category-thumbnail')
              }
              width={32}
              height={32}
              style={{ width: 'auto' }}
            />
          </div>
        )}
        <span
          className={cn('capitalize py-0.5', {
            'font-bold': isTopLevel,
            'text-brand-light': isSelected,
            'text-brand-dark': !isSelected,
          })}
        >
          {getFilterNameForLanguage(menuItem, lang)}
        </span>
        {isSelected && (
          <button
            onClick={handleClearCategory}
            className="ltr:ml-auto rtl:mr-auto p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            title={t('text-clear')}
          >
            <IoClose className="w-3 h-3 text-brand-light" />
          </button>
        )}
      </button>
    </li>
  );
}

function CategoryFilterTree({
  items,
  className,
  lang,
}: {
  items: FilterItem[];
  className?: string;
  lang: string;
}) {
  // Flatten the tree structure to show all categories at once
  const flattenCategories = (
    categories: FilterItem[],
    depth = 0,
  ): FilterItemWithDepth[] => {
    let result: FilterItemWithDepth[] = [];

    categories.forEach((category) => {
      result.push({ ...category, depth });
      if (category.children && category.children.length > 0) {
        result = result.concat(flattenCategories(category.children, depth + 1));
      }
    });
    return result;
  };

  const flattenedItems = flattenCategories(items);

  return (
    <ul
      className={cn(className, 'list-none p-0 m-0')}
      style={{
        listStyle: 'none',
        listStyleType: 'none',
        padding: 0,
        margin: 0,
      }}
    >
      {flattenedItems?.map((item) => (
        <CategoryFilterTreeItem
          key={`${item.slug}-key-${item.id}`}
          item={item}
          depth={item.depth || 0}
          lang={lang}
        />
      ))}
    </ul>
  );
}

export default CategoryFilterTree;
